import type { TaskTemplateCategory } from "./types";

export const MSP_SALES_TEMPLATES: TaskTemplateCategory = {
  id: "sales",
  name: "Sales & Marketing",
  description: "Templates for sales and marketing activities",
  templates: [
    {
      id: "create-proposal",
      name: "Create Client Proposal",
      description: "Draft and prepare a service proposal for potential client",
      title: "Create Proposal: [Client Name]",
      description_text: `Create comprehensive service proposal including:

**Proposal Sections:**
- [ ] Executive summary
- [ ] Current state assessment
- [ ] Recommended services
- [ ] Pricing and terms
- [ ] Implementation timeline
- [ ] Support and maintenance

**Next Steps:**
- Review with team
- Schedule client presentation
- Follow up timeline`,
      tags: ["proposal", "sales", "pricing", "presentation"],
      priority: "medium",
      status: "pending",
      category: "sales",
    },
    {
      id: "follow-up-prospect",
      name: "Follow Up with Prospect",
      description: "Follow up with potential client after initial meeting",
      title: "Follow Up: [Client Name]",
      description_text: `Follow up with prospect to advance the sales process:

**Follow-up Items:**
- [ ] Send requested information
- [ ] Answer any questions
- [ ] Schedule next meeting
- [ ] Provide references if needed

**Timeline:**
- Initial follow-up: Within 24 hours
- Next touchpoint: [Date]`,
      tags: ["follow-up", "sales", "prospect", "meeting"],
      priority: "medium",
      status: "pending",
      category: "sales",
    },
    {
      id: "cold-outreach-campaign",
      name: "Cold Outreach Campaign",
      description: "Systematic outreach to potential new clients",
      title: "Cold Outreach: [Target Industry/Company Type]",
      description_text: `Execute targeted cold outreach campaign:

**Target Research:**
- [ ] Identify 50+ prospects in target vertical
- [ ] Research company size, tech stack, pain points
- [ ] Find decision maker contact information
- [ ] Prepare personalized messaging

**Outreach Sequence:**
- [ ] Email 1: Introduction and value proposition
- [ ] Email 2: Case study and social proof
- [ ] Email 3: Free assessment offer
- [ ] LinkedIn connection and follow-up
- [ ] Phone call attempt

**Tracking & Follow-up:**
- [ ] Log all interactions in CRM
- [ ] Schedule follow-up tasks
- [ ] Measure response rates and optimize`,
      tags: ["cold-outreach", "prospecting", "lead-generation", "email-campaign"],
      priority: "medium",
      status: "pending",
      category: "sales",
    },
    {
      id: "referral-program-outreach",
      name: "Referral Program Outreach",
      description: "Activate existing clients for referrals and testimonials",
      title: "Referral Outreach: [Client Name]",
      description_text: `Leverage existing client relationships for referrals:

**Client Relationship Review:**
- [ ] Assess client satisfaction and success metrics
- [ ] Identify key contacts and champions
- [ ] Review recent wins and value delivered

**Referral Activation:**
- [ ] Schedule check-in call to discuss satisfaction
- [ ] Present referral program benefits and incentives
- [ ] Request specific referrals to similar companies
- [ ] Ask for testimonials and case study participation

**Follow-up Actions:**
- [ ] Send referral program details and materials
- [ ] Schedule introduction calls with referrals
- [ ] Track referral pipeline and conversion`,
      tags: ["referrals", "client-relations", "testimonials", "case-studies"],
      priority: "medium",
      status: "pending",
      category: "sales",
    },
    {
      id: "upsell-opportunity-assessment",
      name: "Upsell Opportunity Assessment",
      description: "Identify and pursue upsell opportunities with existing clients",
      title: "Upsell Assessment: [Client Name]",
      description_text: `Systematically identify upsell opportunities:

**Current Service Analysis:**
- [ ] Review existing service delivery and utilization
- [ ] Identify gaps in current service offering
- [ ] Assess client growth and changing needs
- [ ] Analyze contract renewal timeline

**Upsell Opportunities:**
- [ ] Additional managed services (SOC, vCIO, compliance)
- [ ] Security enhancements (MFA, endpoint protection, training)
- [ ] Infrastructure upgrades (cloud migration, backup improvements)
- [ ] New technology implementations (collaboration, productivity)

**Presentation & Closing:**
- [ ] Prepare ROI analysis and business case
- [ ] Schedule executive presentation
- [ ] Present proposal with implementation timeline
- [ ] Follow up and negotiate terms`,
      tags: ["upsell", "cross-sell", "client-growth", "revenue-expansion"],
      priority: "high",
      status: "pending",
      category: "sales",
    },
    {
      id: "competitive-analysis",
      name: "Competitive Analysis & Positioning",
      description: "Research competitors and position our services advantageously",
      title: "Competitive Analysis: [Market/Client Type]",
      description_text: `Conduct competitive analysis for better positioning:

**Competitor Research:**
- [ ] Identify top 5 competitors in target market
- [ ] Analyze their service offerings and pricing
- [ ] Review their marketing materials and case studies
- [ ] Assess their strengths and weaknesses

**Differentiation Strategy:**
- [ ] Highlight our unique value propositions
- [ ] Develop competitive battle cards
- [ ] Create objection handling responses
- [ ] Prepare win/loss analysis framework

**Market Positioning:**
- [ ] Update sales materials with competitive insights
- [ ] Train team on competitive advantages
- [ ] Develop targeted messaging for different segments`,
      tags: ["competitive-analysis", "positioning", "market-research", "strategy"],
      priority: "medium",
      status: "pending",
      category: "sales",
    },
  ],
};
